import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { setBooleanAttr } from '../../utils/ids-attribute-utils/ids-attribute-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsOrientationMixin from '../../mixins/ids-orientation-mixin/ids-orientation-mixin';
import IdsElement from '../../core/ids-element';

import './ids-tab-content';

import styles from './ids-tabs-context.scss';

import type IdsTabContent from './ids-tab-content';
import type IdsTabs from './ids-tabs';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { isIdsValidatedElement, type IdsValidateEvent } from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import type IdsTab from './ids-tab';
import { getSpecs } from '../../utils/ids-device-env-specs-utils/ids-device-env-specs-utils';

const Base = IdsOrientationMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Tabs Context Component
 * @type {IdsTabsContext}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @part container - the container of all tabs
 */
@customElement('ids-tabs-context')
@scss(styles)
export default class IdsTabsContext extends Base {
  /** Reference to the internal tabs list element */
  tabsListElem?: IdsTabs | null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.onEvent('tabselect', this, (e: { stopPropagation: () => void; detail: { selected: boolean, value: any }; target: { value: any; onAction?: CallableFunction }; }) => {
      e.stopPropagation();
      this.value = e.detail.value ? e.detail.value : e.target.value;
    });

    // On `tabremove` events, remove a tab's corresponding content pane
    this.onEvent('tabremove', this, (e: CustomEvent) => {
      e.stopPropagation();
      const content = this.querySelector(`ids-tab-content[value="${e.detail.value}"]`);
      content?.remove();
    });

    // On `subtabremove` events, remove a sub tab's corresponding content pane
    this.onEvent('subtabremove', this, (e: CustomEvent) => {
      e.stopPropagation();
      const content = this.querySelector(`ids-tab-content[value="${e.detail.value}"]`);
      content?.remove();
    });

    // Set active pane when content is inserted dynamically
    this.onEvent('slotchange', this.container, () => {
      this.initTabContentIds();
      this.#changeContentPane(this.value, this.value);
    });

    this.onEvent('validate', this, (e: IdsValidateEvent) => {
      const content = e.detail.elem?.closest<IdsTabContent>('ids-tab-content');
      if (!content) {
        return;
      }
      const tab = this.tabList?.tabs.find(({ value }) => value === content.value);
      if (!tab) {
        return;
      }
      const validatedElements = [...content.querySelectorAll('[validate]')].filter(isIdsValidatedElement);
      tab.hasError = validatedElements.some((el) => el.validationMessagesCount > 0);
    });

    this.#afterConnectedCallback();
    this.initTabContentIds();

    // initialize tablist element reference
    this.tabsListElem = this.querySelector('ids-tabs')!;
    this.tabList.toggleAttribute(attributes.HIDE_HEADERS, this.hideHeaders);
  }

  /**
   * Handles initialization after the component is connected to the DOM
   * @private
   */
  #afterConnectedCallback() {
    this.value = this.querySelector<IdsTabContent>('[selected]')?.value ?? null;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.AUTO_FIT,
      attributes.HIDE_HEADERS,
      attributes.VALUE
    ];
  }

  template() {
    return '<slot></slot>';
  }

  /**
   * @readonly
   * @returns {IdsTabs} reference to the internal IdsTabs list
   */
  get tabList() {
    if (!this.tabsListElem) {
      this.tabsListElem = this.querySelector<IdsTabs>('ids-tabs')!;
    }

    return this.tabsListElem;
  }

  /**
   * Set the tabs context element to auto fit to its parent container's size
   * @param {boolean|string|null} value The auto fit
   */
  set autoFit(value) {
    setBooleanAttr(attributes.AUTO_FIT, this, stringToBool(value));
  }

  /**
   * Get the auto fit setting
   * @returns {boolean|string|null} The auto fit value
   */
  get autoFit(): boolean | string | null {
    return this.hasAttribute(attributes.AUTO_FIT);
  }

  /** @param {string} value The value representing a currently selected tab */
  set value(value: string | null) {
    const currentValue = this.getAttribute(attributes.VALUE);
    if (currentValue !== value) {
      this.setAttribute(attributes.VALUE, String(value));
      this.#changeContentPane(currentValue, value);
    }
  }

  /**
   * Get the value of the currently selected tab
   * @returns {string|null} The current tab value
   */
  get value() {
    return this.getAttribute(attributes.VALUE);
  }

  /**
   * Sets whether to hide tab headers
   * @param {boolean | string} value True to hide headers
   */
  set hideHeaders(value: boolean | string) {
    const val = stringToBool(value);
    this.toggleAttribute(attributes.HIDE_HEADERS, val);

    if (this.tabList) {
      this.tabList.toggleAttribute(attributes.HIDE_HEADERS, val);
    }
  }

  /**
   * @returns {boolean} True if headers are hidden
   */
  get hideHeaders(): boolean {
    return this.hasAttribute(attributes.HIDE_HEADERS);
  }

  /**
   * Switches the "Active" content pane associated with this Tabs Context
   * @param {string} currentValue the value of the current pane, used to make it inactive
   * @param {string} newValue the value of the new pane, used to make it active
   */
  #changeContentPane(currentValue: any, newValue: any) {
    const contentPanes = [...this.querySelectorAll<IdsTabContent>('ids-tab-content')];
    const currentPane = contentPanes.find((el) => el.value === currentValue);
    const targetPane = contentPanes.find((el) => el.value === newValue);
    if (currentPane) currentPane.active = false;
    if (targetPane) targetPane.active = true;

    // Hack for tab display/text issues in Safari
    if (getSpecs().browser === 'Safari') {
      const tabs = [...this.querySelectorAll<IdsTabContent>('ids-tab')];
      document.body.style.display = 'initial';
      const activeTab = this.querySelector<IdsTabContent>('ids-tab-content[active]');
      if (activeTab) (activeTab.container as IdsTabContent).style.display = 'initial';

      tabs.forEach((tab) => {
        (tab.container as IdsTab).style.display = 'initial';
      });
      requestAnimationFrame(() => {
        tabs.forEach((tab) => {
          (tab.container as IdsTab).style.display = 'flex';
        });
        document.body.style.display = 'block';
        if (activeTab) (activeTab.container as IdsTabContent).style.display = 'block';
      });
    }
  }

  /**
   * Assigns panel IDs and aria-controls to all tab-content elements in a single pass,
   * grouped by value to handle duplicates (e.g. tabpanel-value1-1, tabpanel-value1-2).
   */
  initTabContentIds(): void {
    const counts = new Map<string, number>();
    this.querySelectorAll<IdsTabContent>('ids-tab-content').forEach((content) => {
      const { value } = content;
      if (!value) return;
      const index = (counts.get(value) ?? 0) + 1;
      counts.set(value, index);
      const panelId = `tabpanel-${value}-${index}`;
      content.setAttribute(attributes.ID, panelId);
      content.setAttribute(htmlAttributes.ARIA_LABELLED_BY, `tab-${value}`);
      const tab = this.querySelector(`ids-tab[value="${value}"]`);
      tab?.setAttribute(htmlAttributes.ARIA_CONTROLS, panelId);
    });
  }

  /**
   * Inherited from IdsOrientationMixin
   */
  onOrientationRefresh(): void {
    if (this.orientation) this.tabList?.setAttribute(attributes.ORIENTATION, this.orientation);
  }
}
